import axios, { AxiosHeaders } from 'axios';
import sessionAPI from '@/features/session/api/sessionAPI';
import appLocalStorage from '@/features/session/store/appLocalStorage';

// Axios instance for authorized API requests, with base URL and default headers
const authorizedAPIInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
  },
});

const getSelectedPlant = (): string | null => {
  return appLocalStorage.getProductionUnit();
};

authorizedAPIInstance.interceptors.request.use(
  async requestConfig => {
    if (!requestConfig.headers) {
      requestConfig.headers = new AxiosHeaders();
    }

    const authToken = appLocalStorage.getAuthToken();
    if (authToken) {
      requestConfig.headers['Authorization'] = authToken;
    }
    return requestConfig;
  },
  error => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Request interceptor: adds userContext param to every request
authorizedAPIInstance.interceptors.request.use(
  async requestConfig => {
    requestConfig.params = {
      plant: getSelectedPlant(),
      ...requestConfig.params,
    };
    return requestConfig;
  },
  error => {
    return Promise.reject(error);
  }
);

// Clears local storage and redirects to login page
const redirectToLogin = () => {
  const path = '/';
  appLocalStorage.clearLocalStorage();
  window.location.href = path;
};

// Sets up response interceptor for token refresh and error handling
export const setupAPIInstanceInterceptor = () => {
  authorizedAPIInstance.interceptors.response.use(
    response => response,
    async error => {
      console.log(error);
      const originalRequest = error.config;
      // If unauthorized, try to refresh token
      if (error.response.status === 401 && !originalRequest._retry) {
        // Skip retry/redirect for requests that handle 401 themselves
        if (originalRequest._skipAuthRedirect) {
          return Promise.reject(error);
        }
        originalRequest._retry = true;
        const tokenData = appLocalStorage.getTokenData();

        if (!tokenData?.refresh_token) {
          redirectToLogin();
          return Promise.reject(error);
        }

        try {
          const tokens = await sessionAPI.refreshAccessToken(
            tokenData.refresh_token
          );
          if (tokens.status !== 200) {
            redirectToLogin();
            return await Promise.reject(error);
          }
          appLocalStorage.setTokenData(tokens.data);
          axios.defaults.headers.common.Authorization =
            appLocalStorage.getAuthToken() || '';
          return await authorizedAPIInstance(originalRequest);
        } catch (err) {
          redirectToLogin();
          return Promise.reject(err);
        }
      }
      return Promise.reject(error);
    }
  );
};

// Export the configured axios instance
export default authorizedAPIInstance;
